import './WizardForm.component.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { change, destroy, Field, initialize, reduxForm, reset, untouch } from 'redux-form';

import { socialProjectsLink } from '../../../assets/constants/external-links';
import { ERoutes } from '../../../assets/constants/routes';
import { EEndpoint } from '../../../config/endpoint.config';
import { EFormName } from '../../../config/form-names.config';
import { IAbcListButton } from '../../../domain/abc-list.interface';
import { IContactComplete } from '../../../domain/contact.interface';
import {
  EDatesKeys,
  EFeeType,
  EGroupStateKeys,
  ERefound,
  IGroup,
  IGroupWizardMember,
  TDatesKeys,
  TRefound,
} from '../../../domain/group.interface';
import { EInput } from '../../../domain/input.interface';
import { ENotification } from '../../../domain/notification.interface';
import {
  EItemButtons,
  EUserType,
  externalUserConfig,
  inactiveUserConfig,
  ITypeaheadItem,
  ITypeaheadList,
  minQueryLength,
  optionContactConfig,
  optionUserConfig,
  optionWizardUserConfig,
} from '../../../domain/typeahead.interfaces';
import { EWishStatus, IWish } from '../../../domain/wish.interface';
import { HttpService } from '../../../services/services.index';
import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import { makeSelectForm } from '../../../store/form/form.selectors';
import { editGroup, loadGroupComplete, newGroup, resetGroup } from '../../../store/grouplist/grouplist.actions';
import { makeSelectGroupComplete } from '../../../store/grouplist/grouplist.selectors';
import { returnNotification } from '../../../store/render-controller/render-controller.actions';
import { getContacts, getUsers, resetUsers } from '../../../store/users/users.actions';
import { makeSelectContacts, makeSelectUsers } from '../../../store/users/users.selectors';
import { typeaheadItemFormatter } from '../../../utils/formatter';
import { formatCurrencyToNumber, formatNumberToCurrency, normalizeCurrency } from '../../../utils/FormNormalizers';
import { isEmail, required } from '../../../utils/FormValidators';
import {
  AbcListComponent,
  DatepickerComponent,
  InputFieldComponent,
  TypeaheadComponent,
  WishCardComponent,
  WishlistName,
} from '../../atoms/atoms.index';
import { ImageUploaderComponent, NavbarComponent, NavigationMenuComponent } from '../../organisms/organisms.index';

const steps = [
  {
    current: 0,
    step1: 'active',
    step2: 'inactive',
    step3: 'inactive',
    progwidth: '0%',
    prev: 'none',
    next: 'block',
    fields: ['name'],
  },
  {
    current: 1,
    step1: 'done',
    step2: 'active',
    step3: 'inactive',
    progwidth: '50%',
    prev: 'block',
    next: 'block',
    fields: ['groupFee', 'wishes'],
  },
  {
    current: 2,
    step1: 'done',
    step2: 'done',
    step3: 'active',
    progwidth: '100%',
    prev: 'block',
    next: 'none',
    fields: [
      'payLimitDate',
      'buyLimitDate',
      'deliveryLimitDate',
      'amountsPollLimitDate',
      'amount',
    ],
  },
  {
    current: 3,
    step1: 'done',
    step2: 'done',
    step3: 'done',
    progwidth: '100%',
    prev: 'none',
    next: 'none',
    fields: [],
  },
];

function WizardForm(props: any) {
  const dispatch = useDispatch();
  const httpService = new HttpService();
  const group = useSelector(makeSelectGroupComplete());
  const form: any = useSelector(makeSelectForm());
  const currentUser = useSelector(makeSelectCurrentUser());
  const [isEditMode, setIsEditMode] = useState(false);
  const [state, setState] = useState({
    currentStep: steps[0],
  });
  const [triggers, setTriggers] = useState([false, false, false]);

  const {
    current,
    step1,
    step2,
    step3,
    progwidth,
    prev,
    next,
    fields,
  } = state.currentStep;

  const groupId = props.match.params.groupId;

  const requiredField =
    state.currentStep.current === 0
      ? document.querySelector('#groupName')
      : document.querySelector('#groupAmount');

  function checkErrors() {
    if (!form[EFormName.WIZARD].hasOwnProperty('syncErrors')) {
      return false;
    }
    return !!fields.filter(el => form[EFormName.WIZARD].syncErrors[el]).length;
  }

  function setStep(step: number) {
    if (step < 0 || step > steps.length) {
      return;
    }
    if (state.currentStep.current > step) {
      setState({ currentStep: steps[step] });
    }
    if (checkErrors()) {
      const newTriggers = [...triggers];
      newTriggers[state.currentStep.current] = true;
      setTriggers(newTriggers);
      (requiredField as HTMLInputElement).focus();
      return;
    }
    setState({ currentStep: steps[step] });
  }

  function handleResetForm(e: any) {
    e.preventDefault();
    dispatch(
      returnNotification({
        type: ENotification.WARNING,
        msg1: 'warning.confirmation.message',
        dispatch: { func: reset, param: [EFormName.WIZARD] },
        cb: { func: props.history.push, param: ERoutes.GROUP_LIST },
      }),
    );
  }

  function initializeForm(apiGroup: IGroup) {
    const {
      name,
      amountReturnType: apiAmountReturnType,
      amount: apiAmount,
      payLimitDate,
      buyLimitDate,
      deliveryLimitDate,
      members,
      wishes: apiWishes,
      feeType,
    } = apiGroup;

    const groupForm: any = {
      name: name,
    };

    const apiDates: any = {
      payLimitDate: undefined,
      buyLimitDate: undefined,
      deliveryLimitDate: undefined,
    };

    if (!!members.length) {
      const apiMembers = members.map((member: IGroupWizardMember) => {
        const unallowedButtons = member.hasPaid ? [EItemButtons.DELETE] : [];
        if (member.isExternal) {
          return typeaheadItemFormatter(
            externalUserConfig,
            member,
            EUserType.EXTERNAL,
            unallowedButtons,
          );
        } else if (!member.isActive) {
          return typeaheadItemFormatter(
            inactiveUserConfig,
            member,
            EUserType.INACTIVE,
            unallowedButtons,
          );
        } else {
          return typeaheadItemFormatter(
            optionWizardUserConfig,
            member,
            EUserType.CONTACT,
            unallowedButtons,
          );
        }
      });
      setOldSelectedMembers(apiMembers);
    }

    if (!!apiAmount && !isNaN(apiAmount)) {
      const formattedAmount = formatNumberToCurrency(apiAmount);
      setAmount(formattedAmount);
    }

    if (!_.isNil(feeType)) {
      setAmountType(feeType);
    }

    if (apiAmountReturnType !== null) {
      setAmountReturnType(apiAmountReturnType);
    }

    if (!!apiWishes && !!apiWishes.length) {
      setWishes(apiWishes);
    }

    if (!!buyLimitDate) {
      apiDates.buyLimitDate = buyLimitDate;
    }

    if (!!payLimitDate) {
      apiDates.payLimitDate = payLimitDate;
    }

    if (!!deliveryLimitDate) {
      apiDates.deliveryLimitDate = deliveryLimitDate;
    }

    setDates(apiDates);

    dispatch(initialize(EFormName.WIZARD, groupForm));
  }

  function handleSubmit() {
    if (checkErrors()) {
      return;
    }
    const body = {
      name: form[EFormName.WIZARD].values.name,
      members: [
        ...newSelectedMembers.map((member: any) => member.original),
        ...oldSelectedMembers
          .filter((m: any) => !m.original.isExternal)
          .map((member: any) => member.original),
        ...customMembers,
      ],
      externalMembers: [
        ...fakeMembers.map((member: { name: string }) => member.name),
        ...oldSelectedMembers
          .filter((m: any) => m.original.isExternal)
          .map((member: any) => member.original.name),
      ],
      amount:
        !!amount && amount !== null
          ? formatCurrencyToNumber(amount)
          : !!form[EFormName.WIZARD].values.amount &&
            form[EFormName.WIZARD].values.amount !== null
          ? formatCurrencyToNumber(form[EFormName.WIZARD].values.amount)
          : null,
      feeType: amountType,
      amountReturnType: amountReturnType,
      wishes: wishes.map((wish: IWish) => wish.id),
      payLimitDate: !!dates.payLimitDate
        ? new Date(Date.parse(dates.payLimitDate as any))
        : undefined,
      buyLimitDate: !!dates.buyLimitDate
        ? new Date(Date.parse(dates.buyLimitDate as any))
        : undefined,
      deliveryLimitDate: !!dates.deliveryLimitDate
        ? new Date(Date.parse(dates.deliveryLimitDate as any))
        : undefined,
    };
    if (isEditMode) {
      dispatch(editGroup(body, images, groupId));
    } else {
      dispatch(newGroup(body, images));
    }
  }

  useEffect(() => {
    if (!!groupId) {
      setIsEditMode(true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!groupId) {
      return;
    }
    dispatch(loadGroupComplete(groupId));
    return () => {
      dispatch(resetGroup(EGroupStateKeys.groupComplete));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!Object.keys(group).length) {
      return;
    }
    initializeForm(group);
    return () => {
      dispatch(destroy(EFormName.WIZARD));
    };
    // eslint-disable-next-line
  }, [group]);

  const users = useSelector(makeSelectUsers());
  const [query, setQuery] = useState<string | undefined>('');
  const [typeaheadMembersOptions, setTypeaheadMembersOptions] = useState<any[]>(
    [],
  );
  const [typeaheadPresentsOptions, setTypeaheadPresentsOptions] = useState<
    any[]
  >([]);

  useEffect(() => {
    if (!query || query.trim().length < minQueryLength) {
      dispatch(resetUsers());
      return;
    }
    debouncedFetchUsers();
    // eslint-disable-next-line
  }, [query]);

  const handleInputChange = (pattern: string) => {
    setQuery(pattern.trim().toLowerCase());
  };

  const fetchUsers = () => {
    dispatch(getUsers(query as string));
    return true;
  };

  const debouncedFetchUsers = _.debounce(fetchUsers, 300, { maxWait: 1000 });

  useEffect(() => {
    const noSelfUsers = users.filter((el: any) => el.id !== currentUser.id);
    const filteredUsers = noSelfUsers.filter((el: any) => {
      if (!!oldSelectedMembers.length) {
        return (
          !newSelectedMembers.some(x => Number(x.id) === Number(el.id)) &&
          !oldSelectedMembers.some(x => Number(x.id) === Number(el.id))
        );
      } else {
        return !newSelectedMembers.some(x => Number(x.id) === Number(el.id));
      }
    });
    const apiContacts = filteredUsers
      .filter((el: any) => el.isContact)
      .map((el: any) =>
        typeaheadItemFormatter(optionContactConfig, el, EUserType.CONTACT),
      );
    const apiUsers = filteredUsers
      .filter((el: any) => !el.isContact)
      .map((el: any) =>
        typeaheadItemFormatter(optionUserConfig, el, EUserType.USER),
      );
    const typeaheadMembersOptionsConfig: ITypeaheadList[] = [
      {
        title: 'contacts',
        data: apiContacts,
      },
      {
        title: 'users',
        data: apiUsers,
      },
    ];
    setTypeaheadMembersOptions(typeaheadMembersOptionsConfig);
    // eslint-disable-next-line
  }, [users]);

  useEffect(() => {
    const apiCurrentUser = users
      .filter((el: any) => el.id === currentUser.id)
      .map((el: any) =>
        typeaheadItemFormatter(optionUserConfig, el, EUserType.SELF),
      );
    const apiContacts = users
      .filter((el: any) => el.isContact)
      .map((el: any) =>
        typeaheadItemFormatter(optionContactConfig, el, EUserType.CONTACT),
      );
    const apiUsers = users
      .filter((el: any) => el.id !== currentUser.id && !el.isContact)
      .map((el: any) =>
        typeaheadItemFormatter(optionUserConfig, el, EUserType.USER),
      );
    const typeaheadPresentsOptionsConfig: ITypeaheadList[] = [
      {
        title: 'myself',
        data: apiCurrentUser,
        onClickAction: handleAddNewSelectedProta,
      },
      {
        title: 'contacts',
        data: apiContacts,
        onClickAction: handleAddNewSelectedProta,
      },
      {
        title: 'users',
        data: apiUsers,
        onClickAction: handleAddNewSelectedProta,
      },
    ];
    setTypeaheadPresentsOptions(typeaheadPresentsOptionsConfig);
    // eslint-disable-next-line
  }, [users]);

  const [protaRef, setProtaRef] = useState<any>(undefined);
  const [newSelectedProta, setNewSelectedProta] = useState<ITypeaheadItem[]>(
    [],
  );

  const handleOnFocusProta = () => {
    dispatch(resetUsers());
    setNewSelectedProta([]);
    setSelectedWishList([]);
    protaRef.getInstance().clear();
    setQuery('');
  };

  const handleAddNewSelectedProta = (protaSelected: any) => {
    setNewSelectedProta([protaSelected]);
  };

  const [images, setImages] = useState<any>({});

  function handleNewImages(body: any, imgName: string) {
    const newImages = images;
    newImages[imgName] = body;
    setImages(newImages);
  }

  const [membersRef, setMembersRef] = useState<any>(undefined);
  const [newSelectedMembers, setNewSelectedMembers] = useState<
    ITypeaheadItem[]
  >([]);
  const [oldSelectedMembers, setOldSelectedMembers] = useState<
    ITypeaheadItem[]
  >([]);
  const [customMembers, setCustomMembers] = useState<{ email: string }[]>([]);
  const [fakeMembers, setFakeMembers] = useState<{ name: string }[]>([]);
  const contacts = useSelector(makeSelectContacts());
  const [contactList, setContactList] = useState<IContactComplete[]>([]);
  const buttonListAConfig: IAbcListButton[] = [
    { icon: 'plus-circle', action: handleAddNewSelectedMemberFromAbcList },
  ];

  useEffect(() => {
    dispatch(getContacts());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const newContacts = contacts.filter((contact: IContactComplete) => {
      if (!!oldSelectedMembers.length) {
        return (
          !newSelectedMembers.some(
            x => Number(x.id) === Number(contact.user.id),
          ) &&
          !oldSelectedMembers.some(
            x => Number(x.id) === Number(contact.user.id),
          )
        );
      } else {
        return !newSelectedMembers.some(
          x => Number(x.id) === Number(contact.id),
        );
      }
    });
    setContactList(newContacts);
  }, [contacts, newSelectedMembers, oldSelectedMembers]);

  const handleOnBlurMembers = () => {
    dispatch(resetUsers());
    membersRef.getInstance().clear();
    setQuery('');
  };

  const handleAddNewSelectedMember = (
    membersSelectedArray: ITypeaheadItem[],
  ) => {
    setNewSelectedMembers(membersSelectedArray);
  };

  function handleAddNewSelectedMemberFromAbcList(contact: IContactComplete) {
    const newContact = typeaheadItemFormatter(optionUserConfig, contact.user);
    setOldSelectedMembers((selected: ITypeaheadItem[]) => [
      ...selected,
      newContact,
    ]);
  }

  const handleRemoveOldSelectedMember = (member: ITypeaheadItem) => {
    setOldSelectedMembers(
      oldSelectedMembers.filter(
        (oldMember: ITypeaheadItem) => oldMember.id !== member.id,
      ),
    );
  };

  const handleAddCustomMember = (value: string) => {
    dispatch(change(EFormName.WIZARD, 'customMember', ''));
    const check1 = customMembers.filter(
      (member: any) => member.email === value.trim().toLowerCase(),
    );
    if (!!check1.length) {
      return;
    }
    const check2 = oldSelectedMembers.filter(
      (member: any) => member.title === value.trim().toLowerCase(),
    );
    if (!!check2.length) {
      return;
    }
    const checkMail = !!form[EFormName.WIZARD].syncErrors
      ? !!form[EFormName.WIZARD].syncErrors.customMember
        ? true
        : false
      : false;
    if (checkMail) {
      return;
    }
    setCustomMembers([...customMembers, { email: value.trim().toLowerCase() }]);
  };

  const handleRemoveCustomMember = (index: number) => {
    setCustomMembers(
      customMembers.filter(
        (member: { email: string }, idx: number) => idx !== index,
      ),
    );
  };

  const handleAddFakeMember = (value: string) => {
    dispatch(change(EFormName.WIZARD, 'fakeMember', ''));
    const check1 = fakeMembers.filter(
      (member: any) => member.name === value.trim().toLowerCase(),
    );
    if (!!check1.length || !value.trim()) {
      return;
    }
    setFakeMembers([...fakeMembers, { name: value.trim().toLowerCase() }]);
  };

  const handleRemoveFakeMember = (index: number) => {
    setFakeMembers(
      fakeMembers.filter(
        (member: { name: string }, idx: number) => idx !== index,
      ),
    );
  };

  const [amount, setAmount] = useState<string | undefined>(undefined);

  const [amountType, setAmountType] = useState<EFeeType>(EFeeType.UNDEFINED);

  const [amountReturnType, setAmountReturnType] = useState<TRefound>(
    ERefound.TO_ADMIN,
  );

  const onResetMoneybank = () => {
    setAmountType(EFeeType.UNDEFINED);
    handleRemoveAmount();
    setAmountReturnType(ERefound.TO_ADMIN);
    handleDate(undefined, EDatesKeys.payLimitDate);
  };

  /* const handleAddAmount = (value: string) => {
    let newValue = value;
    dispatch(change(EFormName.WIZARD, 'amount', ''));
    if (!value) {
      return;
    } else if (value.indexOf(',') <= 0) {
      newValue += ',00';
    }
    setAmount(newValue);
  }; */

  const handleRemoveAmount = () => {
    dispatch(untouch(EFormName.WIZARD, 'amount'));
    setAmount(undefined);
  };

  const [wishListOptions, setWishListOptions] = useState<any[]>([]);
  const [wishListId, setWishListId] = useState('');
  const [selectedWishList, setSelectedWishList] = useState<any>([]);
  const [wishes, setWishes] = useState<any[]>([]);

  const fetchUserWishlistcatalogue = async () => {
    if (!newSelectedProta.length) {
      return;
    }
    const protaId = newSelectedProta[0].id;
    await httpService
      .get(EEndpoint.GET_USER_WISH_LIST_CATALOGUE, {
        params: { userId: protaId },
      })
      .then((res: any) => setWishListOptions(res));
  };

  const fetchUserWishes = async () => {
    if (!wishListId) {
      return;
    }
    await httpService
      .get(EEndpoint.WISH_LIST, {
        params: { wishlistId: wishListId },
        silently: true,
      })
      .then((res: any) =>
        setSelectedWishList(
          res.wishes.filter(
            (wish: any) => wish.status === EWishStatus.AVAILABLE,
          ),
        ),
      );
  };

  const handleRemoveWish = (index: string) => {
    setWishes(wishes.filter((wish: any) => wish.id !== index));
  };

  const handleAddSelectedWish = (idx: number) => {
    if (!!wishes.length && wishes.some((wish: any) => wish.id === idx)) {
      return;
    }
    setWishes([
      ...wishes,
      ...selectedWishList.filter((wish: any) => wish.id === idx),
    ]);
  };

  useEffect(() => {
    fetchUserWishlistcatalogue();
    return () => setWishListOptions([]);
    // eslint-disable-next-line
  }, [newSelectedProta]);

  useEffect(() => {
    fetchUserWishes();
    return () => setSelectedWishList([]);
    // eslint-disable-next-line
  }, [wishListId]);

  const [dates, setDates] = useState({
    payLimitDate: undefined,
    buyLimitDate: undefined,
    deliveryLimitDate: undefined,
  });

  const [initialDates, setInitialDates] = useState({
    payLimitDate: new Date(),
    buyLimitDate: new Date(),
    deliveryLimitDate: new Date(),
  });

  const handleDate = (date: any, name: TDatesKeys) => {
    setDates({ ...dates, [name]: date });
    setInitialDates({ ...initialDates, [name]: new Date() });
  };

  useEffect(() => {
    if (!!dates.buyLimitDate && dates.buyLimitDate !== null) {
      const newInitialDates = { ...initialDates };
      newInitialDates.deliveryLimitDate = dates.buyLimitDate as any;
      setInitialDates(newInitialDates);
    }
    // eslint-disable-next-line
  }, [dates]);

  return (
    <div className='page-container'>
      <NavbarComponent />
      <NavigationMenuComponent {...props} />
      <section className='progressbar-container'>
        <div className='step-wizard' role='navigation'>
          <div className='progress'>
            <div className='progressbar empty'></div>
            <div
              className='progressbar'
              style={{ width: `${progwidth}` }}
            ></div>
          </div>
          <ul>
            <li className={`${step1}`}>
              <button
                onClick={() => {
                  setStep(0);
                }}
              >
                <div className='step'>
                  {current > 0 ? <FontAwesomeIcon icon='check' /> : 1}
                </div>
                <div className='title'>
                  <FormattedMessage id='pages.wizard_form.step1.main_title' />
                </div>
              </button>
            </li>
            <li className={`${step2}`}>
              <button onClick={() => setStep(1)}>
                <div className='step'>
                  {current > 1 ? <FontAwesomeIcon icon='check' /> : 2}
                </div>
                <div className='title'>
                  <FormattedMessage id='pages.wizard_form.step2.main_title' />
                </div>
              </button>
            </li>
            <li className={`${step3}`}>
              <button onClick={() => setStep(2)}>
                <div className='step'>
                  {current > 2 ? <FontAwesomeIcon icon='check' /> : 3}
                </div>
                <div className='title'>
                  <FormattedMessage id='pages.wizard_form.step3.main_title' />
                </div>
              </button>
            </li>
          </ul>
        </div>
      </section>

      <section className='wizard-form-container'>
        <form>
          <div style={{ display: `${current === 0 ? 'block' : 'none'}` }}>
            <h2 className={`${current === 0 ? '' : 'hide'} step-title`}>
              <FormattedMessage id='pages.wizard_form.step1.main_title' />
            </h2>
            <div className='content-wrapper'>
              <div className='mb-3'>
                <h4 className='subtitle'>
                  <FormattedMessage id='pages.wizard_form.step1.group_title' />
                </h4>
                <FormattedMessage id='pages.wizard_form.step1.group_placeholder'>
                  {(txt: any) => (
                    <Field
                      name={'name'}
                      propsId='groupName'
                      component={InputFieldComponent}
                      validate={[required]}
                      label={txt}
                      errorTrigger={triggers[0]}
                    />
                  )}
                </FormattedMessage>
              </div>
              <div className='mb-3'>
                <h4 className='subtitle'>
                  <FormattedMessage id='pages.wizard_form.step1.photo_title' />
                </h4>
                <div>
                  <ImageUploaderComponent
                    name='main_photo'
                    handleNewImages={handleNewImages}
                    maxNumImg={1}
                    propsImages={
                      !!group && group.photoUrl ? group.photoUrl : undefined
                    }
                  />
                </div>
              </div>
              <div className='mb-3'>
                <h4 className='subtitle'>
                  <FormattedMessage id='pages.wizard_form.step1.add_user_title' />
                </h4>
                <p className='text-primary-lg font-weight-light'>
                  <FormattedMessage id='pages.wizard_form.step1.add_user_paragraph1' />
                </p>
                <FormattedMessage id='pages.wizard_form.step1.add_user_placeholder1'>
                  {(txt: any) => (
                    <TypeaheadComponent
                      label={txt}
                      addRef={(r: any) => setMembersRef(r)}
                      handleInputChange={handleInputChange}
                      handleOnBlur={handleOnBlurMembers}
                      options={typeaheadMembersOptions}
                      withList={true}
                      oldSelected={oldSelectedMembers}
                      handleRemoveOldSelected={handleRemoveOldSelectedMember}
                      handleAddNewSelected={handleAddNewSelectedMember}
                    />
                  )}
                </FormattedMessage>
                {!!contactList.length && (
                  <div className='md-card'>
                    <h4 className='subtitle mb-3'>
                      <FormattedMessage id='pages.my-contactlist.list.title' />
                    </h4>
                    <div className='scrollable-50'>
                      <AbcListComponent
                        list={contactList}
                        groupKey='alias'
                        buttonsA={buttonListAConfig}
                      />
                    </div>
                  </div>
                )}
                <p className='text-primary-lg font-weight-light mt-4'>
                  <FormattedMessage id='pages.wizard_form.step1.add_user_paragraph2' />
                </p>
                <FormattedMessage id='pages.wizard_form.step1.add_user_placeholder2'>
                  {(txt: any) => (
                    <Field
                      name='customMember'
                      component={InputFieldComponent}
                      label={txt}
                      enterFunc={handleAddCustomMember}
                      validate={[isEmail]}
                    />
                  )}
                </FormattedMessage>
                <div className='mb-3'>
                  {!!customMembers.length &&
                    customMembers.map(
                      (member: { email: string }, idx: number) => {
                        return (
                          <div
                            key={`member-${idx}`}
                            className={`minicard user-type-${EUserType.INACTIVE}`}
                          >
                            <div className='minicard-image'>
                              <div className='icon-container d-flex justify-container-center align-items-center'>
                                <FontAwesomeIcon icon='user' />
                              </div>
                            </div>
                            <div className='minicard-info'>
                              <h6>{member.email}</h6>
                              <p>
                                ({' '}
                                <FormattedMessage
                                  id={`common.user-type-${EUserType.INACTIVE}`}
                                />{' '}
                                )
                              </p>
                            </div>
                            <div className='minicard-buttons'>
                              <div
                                className='minicard-delete'
                                onClick={() => handleRemoveCustomMember(idx)}
                              >
                                <FontAwesomeIcon icon='trash' />
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                </div>
                <p className='text-primary-lg font-weight-light mt-2'>
                  <FormattedMessage id='pages.wizard_form.step1.add_user_paragraph3' />
                </p>
                <FormattedMessage id='pages.wizard_form.step1.add_user_placeholder3'>
                  {(txt: any) => (
                    <Field
                      name='fakeMember'
                      component={InputFieldComponent}
                      label={txt}
                      enterFunc={handleAddFakeMember}
                    />
                  )}
                </FormattedMessage>
                <div className='mb-3'>
                  {!!fakeMembers.length &&
                    fakeMembers.map((member: { name: string }, idx: number) => {
                      return (
                        <div
                          key={`member-${idx}`}
                          className={`minicard user-type-${EUserType.INACTIVE}`}
                        >
                          <div className='minicard-image'>
                            <div className='icon-container d-flex justify-container-center align-items-center'>
                              <FontAwesomeIcon icon='user' />
                            </div>
                          </div>
                          <div className='minicard-info'>
                            <h6>{member.name}</h6>
                            <p>
                              ({' '}
                              <FormattedMessage
                                id={`common.user-type-${EUserType.EXTERNAL}`}
                              />{' '}
                              )
                            </p>
                          </div>
                          <div className='minicard-buttons'>
                            <div
                              className='minicard-delete'
                              onClick={() => handleRemoveFakeMember(idx)}
                            >
                              <FontAwesomeIcon icon='trash' />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: `${current === 1 ? 'block' : 'none'}` }}>
            <h2 className={`${current === 1 ? '' : 'hide'} step-title`}>
              <FormattedMessage id='pages.wizard_form.step2.main_title' />
            </h2>
            <div className='content-wrapper'>
              <div className='mb-3'>
                <h4 className='subtitle'>
                  <FormattedMessage id='pages.wizard_form.step2.present_title' />
                </h4>
                <p className='text-primary-lg font-weight-light'>
                  <FormattedMessage id='pages.wizard_form.step2.present_paragraph' />
                </p>
                <FormattedMessage id='pages.wizard_form.step1.prota_placeholder1'>
                  {(txt: any) => (
                    <TypeaheadComponent
                      label={txt}
                      addRef={(r: any) => setProtaRef(r)}
                      handleInputChange={handleInputChange}
                      handleOnFocus={handleOnFocusProta}
                      options={typeaheadPresentsOptions}
                    />
                  )}
                </FormattedMessage>
                {!!newSelectedProta.length && (
                  <div className='field-container mb-3'>
                    <FormattedMessage id='pages.wizard_form.step2.present_placeholder1'>
                      {(txt: any) => (
                        <div className='select-wrapper'>
                          <select
                            defaultValue={''}
                            className='field'
                            onChange={(e: any) => setWishListId(e.target.value)}
                          >
                            <option value={''} disabled>
                              {txt}
                            </option>
                            {wishListOptions &&
                              wishListOptions.map((option: any) =>
                              <WishlistName wishlist={option} />,
                              )}
                          </select>
                          <FontAwesomeIcon
                            icon='angle-down'
                            className='select-arrow'
                          />
                        </div>
                      )}
                    </FormattedMessage>
                  </div>
                )}
                {!!selectedWishList.length && (
                  <div className='field-container mb-3'>
                    <FormattedMessage id='pages.wizard_form.step2.present_placeholder2'>
                      {(txt: any) => (
                        <div className='select-wrapper'>
                          <select
                            defaultValue={''}
                            className='field'
                            onChange={(e: any) =>
                              handleAddSelectedWish(Number(e.target.value))
                            }
                          >
                            <option value={''} disabled>
                              {txt}
                            </option>
                            {selectedWishList &&
                              selectedWishList.map((option: any) => (
                                <option value={option.id} key={option.id}>
                                  {option.name}
                                </option>
                              ))}
                          </select>
                          <FontAwesomeIcon
                            icon='angle-down'
                            className='select-arrow'
                          />
                        </div>
                      )}
                    </FormattedMessage>
                  </div>
                )}
                <div className='mb-5'>
                  {!!wishes.length &&
                    wishes.map((wish: any, idx: number) => {
                      return (
                        <WishCardComponent
                          key={idx}
                          wish={wish}
                          readOnly={false}
                          deleteItem={handleRemoveWish}
                        />
                      );
                    })}
                </div>
                <div className='mb-5'>
                  <h4 className='subtitle'>
                    <FormattedMessage id='pages.wizard_form.step2.date_title' />
                  </h4>
                  <div className='mb-3'>
                    <Field
                      name='buyLimitDate'
                      component={DatepickerComponent}
                      formName={EFormName.WIZARD}
                      placeholder='pages.wizard_form.step2.date_buyLimitDate'
                      initialDate={initialDates.buyLimitDate}
                      date={dates.buyLimitDate}
                      maxDate={
                        !!dates.deliveryLimitDate
                          ? dates.deliveryLimitDate
                          : undefined
                      }
                      setLimitDate={handleDate}
                    />
                  </div>
                  <div className='mb-3'>
                    <Field
                      name='deliveryLimitDate'
                      component={DatepickerComponent}
                      formName={EFormName.WIZARD}
                      placeholder='pages.wizard_form.step2.date_deliveryLimitDate'
                      initialDate={initialDates.deliveryLimitDate}
                      date={dates.deliveryLimitDate}
                      setLimitDate={handleDate}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: `${current === 2 ? 'block' : 'none'}` }}>
            <h2 className={`${current === 2 ? '' : 'hide'} step-title`}>
              <FormattedMessage id='pages.wizard_form.step3.main_title' />
            </h2>
            <div className='content-wrapper'>
              <div className='mb-3'>
                <h4 className='subtitle'>
                  <FormattedMessage id='pages.wizard_form.step3.active_moneybank.title' />
                </h4>
                <div className='active-moneybank-info'>
                  {' '}
                  <p className='text-primary-lg font-weight-light'>
                    <FormattedMessage id='pages.wizard_form.step3.active_moneybank.paragraph' />
                  </p>
                  <ul>
                    <li>
                      <FormattedMessage id='pages.wizard_form.step3.active_moneybank.li1' />
                    </li>
                    <li>
                      <FormattedMessage id='pages.wizard_form.step3.active_moneybank.li2' />
                    </li>
                    <li>
                      <FormattedMessage id='pages.wizard_form.step3.active_moneybank.li3' />
                    </li>
                  </ul>
                </div>
                <div className='field-container mb-5'>
                  <div className='radio-container very-small'>
                    <label
                      htmlFor='moneybank-active'
                      className={`radio ${
                        group.firstPayment ? 'disabled' : ''
                      }`}
                    >
                      <input
                        name='active-moneybank'
                        onChange={() => setAmountType(EFeeType.FIXED)}
                        value={EFeeType.FIXED}
                        type='radio'
                        id='moneybank-active'
                        className='hide'
                        disabled={group.firstPayment}
                        checked={amountType !== EFeeType.UNDEFINED}
                      />
                      <span className='label'></span>
                      <p className='input-label'>
                        <FormattedMessage id='common.yes' />
                      </p>
                    </label>
                    <label
                      htmlFor='moneybank-inactive'
                      className={`radio ${
                        group.firstPayment ? 'disabled' : ''
                      }`}
                    >
                      <input
                        name='active-moneybank'
                        onChange={() => onResetMoneybank()}
                        type='radio'
                        id='moneybank-inactive'
                        className='hide'
                        disabled={group.firstPayment}
                        checked={amountType === EFeeType.UNDEFINED}
                      />
                      <span className='label'></span>
                      <p className='input-label'>
                        <FormattedMessage id='common.no' />
                      </p>
                    </label>
                  </div>
                  <p className='smaller'>
                    <FormattedMessage id='pages.wizard_form.step3.active_moneybank.smaller' />
                  </p>
                </div>
              </div>
              {amountType !== EFeeType.UNDEFINED && (
                <React.Fragment>
                  <div className='field-container mb-3'>
                    <h4 className='subtitle'>
                      <FormattedMessage id='pages.wizard_form.step3.fee_title' />
                    </h4>
                    <div className='radio-container'>
                      <label
                        htmlFor='amount-fixed'
                        className={`radio ${
                          group.firstPayment ? 'disabled' : ''
                        }`}
                      >
                        <input
                          name='amount-type-radio'
                          onChange={() => setAmountType(EFeeType.FIXED)}
                          value={EFeeType.FIXED}
                          type='radio'
                          id='amount-fixed'
                          className='hide'
                          disabled={group.firstPayment}
                          checked={amountType === EFeeType.FIXED}
                        />
                        <span className='label'></span>
                        <p className='input-label'>
                          <span className='main'>
                            <FormattedMessage id='pages.wizard_form.step3.amount_type_fixed.main' />
                          </span>
                          <br />
                          <span className='secondary'>
                            <FormattedMessage id='pages.wizard_form.step3.amount_type_fixed.secondary' />
                          </span>
                        </p>
                      </label>
                      <label
                        htmlFor='amount-variable'
                        className={`radio ${
                          group.firstPayment ? 'disabled' : ''
                        }`}
                      >
                        <input
                          name='amount-type-radio'
                          onChange={() => setAmountType(EFeeType.VARIABLE)}
                          value={EFeeType.VARIABLE}
                          type='radio'
                          id='amount-variable'
                          className='hide'
                          disabled={group.firstPayment}
                          checked={
                            amountType === EFeeType.VARIABLE ||
                            amountType === EFeeType.HIDDEN
                          }
                        />
                        <span className='label'></span>
                        <p className='input-label'>
                          <span className='main'>
                            <FormattedMessage id='pages.wizard_form.step3.amount_type_variable.main' />
                          </span>
                          <br />
                          <span className='secondary'>
                            <FormattedMessage id='pages.wizard_form.step3.amount_type_variable.secondary' />
                          </span>
                        </p>
                      </label>
                    </div>
                  </div>
                </React.Fragment>
              )}
              <div className='mb-3'>
                {!amount &&
                  amountType !== EFeeType.UNDEFINED &&
                  !group.firstPayment && (
                    <div>
                      <p className='text-primary-lg font-weight-light'>
                        <FormattedMessage id='pages.wizard_form.step3.fee_paragraph' />
                      </p>
                      <FormattedMessage
                        id={
                          amountType === EFeeType.FIXED
                            ? 'pages.wizard_form.step3.fee_placeholder_fixed'
                            : 'pages.wizard_form.step3.fee_placeholder_variable'
                        }
                      >
                        {(txt: any) => (
                          <Field
                            propsId='groupAmount'
                            name='amount'
                            component={InputFieldComponent}
                            label={txt}
                            type={EInput.TEXT}
                            normalize={(value: number | string) =>
                              normalizeCurrency(value)
                            }
                            validate={
                              amountType === EFeeType.FIXED ? [required] : []
                            }
                            errorTrigger={triggers[2]}
                          />
                        )}
                      </FormattedMessage>
                    </div>
                  )}
                {!!amount && (
                  <div className='mb-5'>
                    <div className='minicard'>
                      <div className='minicard-info'>{amount}</div>
                      <div className='minicard-buttons'>
                        {!group.firstPayment && (
                          <div
                            className='minicard-delete'
                            onClick={() => handleRemoveAmount()}
                          >
                            <FontAwesomeIcon icon='trash' />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {(amountType === EFeeType.VARIABLE ||
                  amountType === EFeeType.HIDDEN) && (
                  <div className='field-container wizard-checkbox mb-5'>
                    <label
                      htmlFor={props.name}
                      className={`checkbox ${
                        group.firstPayment ? 'disabled' : ''
                      }`}
                    >
                      <input
                        type={EInput.CHECKBOX}
                        onChange={(e: any) => {
                          !e.target.checked
                            ? setAmountType(EFeeType.HIDDEN)
                            : setAmountType(EFeeType.VARIABLE);
                        }}
                        checked={amountType === EFeeType.VARIABLE}
                        disabled={group.firstPayment}
                      />
                      <span className='checkmark'></span>
                      <span className='checkbox-text'>
                        <FormattedMessage id='pages.wizard_form.step3.hidden_amount' />
                      </span>
                    </label>
                  </div>
                )}
              </div>
              {amountType !== EFeeType.UNDEFINED && (
                <div className='mb-5'>
                  <h4 className='subtitle'>
                    <FormattedMessage id='pages.wizard_form.step3.refoundType_title' />
                  </h4>
                  <div className='radio-container small'>
                    <label
                      htmlFor='opt2'
                      className={`radio ${
                        group.firstPayment ? 'disabled' : ''
                      }`}
                    >
                      <input
                        name='amountReturnType'
                        value={ERefound.TO_ADMIN}
                        type='radio'
                        id='opt2'
                        className='hide'
                        disabled={group.firstPayment}
                        checked={amountReturnType === ERefound.TO_ADMIN}
                        onChange={(e: any) =>
                          setAmountReturnType(Number(e.target.value))
                        }
                      />
                      <span className='label'></span>
                      <p className='input-label'>
                        <span className='main'>
                          <FormattedMessage
                            id={`common.refound.${ERefound.TO_ADMIN}.main`}
                          />
                        </span>
                        <br />
                        <span className='secondary'>
                          <FormattedMessage
                            id={`common.refound.${ERefound.TO_ADMIN}.secondary`}
                          />
                        </span>
                      </p>
                    </label>

                    <label
                      htmlFor='opt3'
                      className={`radio ${
                        group.firstPayment ? 'disabled' : ''
                      }`}
                    >
                      <input
                        name='amountReturnType'
                        value={ERefound.TO_ONG}
                        type='radio'
                        id='opt3'
                        className='hide'
                        disabled={group.firstPayment}
                        checked={amountReturnType === ERefound.TO_ONG}
                        onChange={(e: any) =>
                          setAmountReturnType(Number(e.target.value))
                        }
                      />
                      <span className='label'></span>
                      <p className='input-label'>
                        <span className='main'>
                          <FormattedMessage
                            id={`common.refound.${ERefound.TO_ONG}.main`}
                          />
                        </span>
                        <br />
                        <span className='secondary'>
                          <FormattedMessage
                            id={`common.refound.${ERefound.TO_ONG}.secondary`}
                          />
                          <a
                            href={socialProjectsLink.link}
                            aria-label={socialProjectsLink.name}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <FontAwesomeIcon icon='info-circle' />
                          </a>
                        </span>
                      </p>
                    </label>
                  </div>
                </div>
              )}
              {(amountType !== EFeeType.UNDEFINED || group.firstPayment) && (
                <div>
                  <h4 className='subtitle'>
                    <FormattedMessage id='pages.wizard_form.step3.date_title' />
                  </h4>
                  <div className='mb-3'>
                    <Field
                      name='payLimitDate'
                      component={DatepickerComponent}
                      formName={EFormName.WIZARD}
                      placeholder='pages.wizard_form.step3.date_payLimitDate'
                      initialDate={initialDates.payLimitDate}
                      date={dates.payLimitDate}
                      setLimitDate={handleDate}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </section>

      <section className='form-button-container mt-6'>
        <div className='left-wfc'>
          <button
            className={`${current === 3 ? 'hide' : ''} btn-lili danger`}
            onClick={(e: any) => handleResetForm(e)}
            style={{ display: current === 3 ? 'none' : 'block' }}
          >
            <FormattedMessage id='common.cancel' />
          </button>
        </div>
        <div className='right-wfc'>
          <button
            className='btn-lili secondary'
            style={{ display: `${prev}` }}
            onClick={() => setStep(current - 1)}
          >
            <FormattedMessage id='common.prev' />
          </button>
          <button
            className='btn-lili secondary'
            style={{ display: `${next}` }}
            onClick={() => setStep(current + 1)}
          >
            <FormattedMessage id='common.next' />
          </button>
          <button
            className='btn-lili'
            onClick={() => {
              setStep(3);
              handleSubmit();
            }}
          >
            <FormattedMessage id='pages.wizard_form.save_group' />
          </button>
        </div>
      </section>
    </div>
  );
}

export default reduxForm({
  form: EFormName.WIZARD,
})(WizardForm);
